import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

type Status = 'idle' | 'loading' | 'success' | 'error';

export default function ContactPage() {
  const [status, setStatus] = useState<Status>('idle');
  const [charCount, setCharCount] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (form: HTMLFormElement) => {
    const errs: Record<string, string> = {};
    const name = (form.elements.namedItem('name') as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value.trim();

    if (!name) errs.name = 'お名前を入力してください';
    if (!email) {
      errs.email = 'メールアドレスを入力してください';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = '正しいメールアドレスを入力してください';
    }
    if (!message) errs.message = 'お問い合わせ内容を入力してください';
    if (message.length > 500) errs.message = '500文字以内で入力してください';
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus('loading');

    const data = new URLSearchParams();
    const elements = form.elements;
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (el.name && el.value) {
        data.append(el.name, el.value);
      }
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d73uqq75hic0eqh30ueg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F4]">
      <Navbar />

      {/* Hero */}
      <div className="bg-[#F0EBE3] pt-28 pb-12">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <nav className="flex items-center justify-center gap-2 text-xs text-[#A0A0A0] mb-4">
            <Link to="/" className="hover:text-[#8FA882] transition-colors cursor-pointer">ホーム</Link>
            <i className="ri-arrow-right-s-line text-[#C0B8B0]" />
            <span className="text-[#6B6B6B]">お問い合わせ</span>
          </nav>
          <h1 className="text-2xl md:text-3xl font-bold text-[#2C2C2C] mb-3">お問い合わせ</h1>
          <p className="text-sm text-[#6B6B6B] leading-relaxed max-w-md mx-auto">
            サイトに関するご質問・ご意見・取材のご依頼など、<br className="hidden sm:inline" />
            お気軽にお送りください。通常2〜3営業日以内にご返信いたします。
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-14">

        {status === 'success' ? (
          <div className="bg-white rounded-xl border border-[#EDE7DE] p-10 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EAF3E7] mx-auto mb-5">
              <i className="ri-check-line text-2xl text-[#8FA882]" />
            </div>
            <h2 className="text-lg font-bold text-[#2C2C2C] mb-2">送信が完了しました</h2>
            <p className="text-sm text-[#6B6B6B] leading-relaxed mb-6">
              お問い合わせいただきありがとうございます。<br />
              内容を確認の上、折り返しご連絡いたします。
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="text-sm text-[#8FA882] hover:text-[#7A9570] underline cursor-pointer transition-colors"
            >
              別のお問い合わせをする
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[#EDE7DE] p-8 md:p-10">

            {status === 'error' && (
              <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line text-red-400 mt-0.5" />
                <p className="text-sm text-red-600">送信中にエラーが発生しました。しばらくしてから再度お試しください。</p>
              </div>
            )}

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              noValidate
              className="space-y-6"
            >
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-[#2C2C2C] mb-1.5">
                  お名前 <span className="text-red-400 text-xs ml-1">必須</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="例：山田 太郎"
                  className={`w-full text-sm px-4 py-3 rounded-lg border bg-[#FAF7F4] text-[#2C2C2C] placeholder-[#B0A89E] outline-none transition-colors focus:border-[#8FA882] focus:bg-white ${errors.name ? 'border-red-300' : 'border-[#E0D8D0]'}`}
                  onChange={() => setErrors((e) => ({ ...e, name: '' }))}
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#2C2C2C] mb-1.5">
                  メールアドレス <span className="text-red-400 text-xs ml-1">必須</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="example@email.com"
                  className={`w-full text-sm px-4 py-3 rounded-lg border bg-[#FAF7F4] text-[#2C2C2C] placeholder-[#B0A89E] outline-none transition-colors focus:border-[#8FA882] focus:bg-white ${errors.email ? 'border-red-300' : 'border-[#E0D8D0]'}`}
                  onChange={() => setErrors((e) => ({ ...e, email: '' }))}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              {/* Category */}
              <div>
                <label htmlFor="category" className="block text-sm font-semibold text-[#2C2C2C] mb-1.5">
                  お問い合わせの種別
                </label>
                <div className="relative">
                  <select
                    id="category"
                    name="category"
                    className="w-full text-sm px-4 py-3 rounded-lg border border-[#E0D8D0] bg-[#FAF7F4] text-[#2C2C2C] outline-none appearance-none focus:border-[#8FA882] focus:bg-white transition-colors cursor-pointer"
                  >
                    <option value="記事・内容に関するご質問">記事・内容に関するご質問</option>
                    <option value="サイトへのご意見・ご感想">サイトへのご意見・ご感想</option>
                    <option value="取材・メディア掲載のご依頼">取材・メディア掲載のご依頼</option>
                    <option value="リンク・転載のご依頼">リンク・転載のご依頼</option>
                    <option value="その他">その他</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center w-5 h-full justify-center">
                    <i className="ri-arrow-down-s-line text-[#A0A0A0]" />
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#2C2C2C] mb-1.5">
                  お問い合わせ内容 <span className="text-red-400 text-xs ml-1">必須</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  maxLength={500}
                  placeholder="ご質問・ご意見などをお書きください（500文字以内）"
                  className={`w-full text-sm px-4 py-3 rounded-lg border bg-[#FAF7F4] text-[#2C2C2C] placeholder-[#B0A89E] outline-none transition-colors focus:border-[#8FA882] focus:bg-white resize-none ${errors.message ? 'border-red-300' : 'border-[#E0D8D0]'}`}
                  onChange={(e) => {
                    setCharCount(e.target.value.length);
                    setErrors((err) => ({ ...err, message: '' }));
                  }}
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.message
                    ? <p className="text-xs text-red-500">{errors.message}</p>
                    : <span />}
                  <p className={`text-xs ml-auto ${charCount > 480 ? 'text-red-400' : 'text-[#A0A0A0]'}`}>
                    {charCount} / 500
                  </p>
                </div>
              </div>

              {/* Privacy Note */}
              <p className="text-xs text-[#A0A0A0] leading-relaxed">
                送信いただいた情報は、お問い合わせ対応の目的のみに使用し、第三者には提供しません。
                詳しくは
                <Link to="/privacy-policy" className="text-[#8FA882] hover:underline ml-0.5 cursor-pointer">
                  プライバシーポリシー
                </Link>
                をご覧ください。
              </p>

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3.5 rounded-lg bg-[#8FA882] text-white text-sm font-semibold hover:bg-[#7A9570] transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    送信中...
                  </span>
                ) : '送信する'}
              </button>
            </form>
          </div>
        )}

        {/* Back link */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#8FA882] hover:text-[#7A9570] transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            トップページへ戻る
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}
